import React, { useRef, useState } from "react";
import { Toast, Modal, Form, Button } from "@douyinfe/semi-ui";
import type {
  BaseFormApi as FormApi,
  FormState,
} from "@douyinfe/semi-foundation/lib/es/form/interface";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@state/store";
import { setLocalStorage } from "@utils/localstorage";
import { LoginEnum } from "@services/account/enum/login.enum";
import { LoginJobPayloadModel } from "@services/home/model/home.model";
import { useTranslation } from "react-i18next";
import { encrypt } from "@utils/encryption.util";
import { useCountDown } from "ahooks";
import { setToken } from "@state/global/reducer";
import { LoginPayloadModel } from "@services/account/model/account.model";
import {
  loginApi,
  sendEmailApi,
  checkNeedOrgnizationApi,
} from "@services/account/account.api";
import {
  asyncEmailValidate,
  codeValidate,
  organizationValidate,
} from "@utils/validator";

import styles from "./index.module.scss";

interface Prop {
  cancel: () => void;
  submit: () => void;
}

const LoginDialog: React.FC<Prop> = ({ cancel, submit }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const formRef = useRef<FormApi<LoginJobPayloadModel>>();
  const [isSendEmail, setIsSendEmail] = useState(false);
  const [emailDate, setEmailDate] = useState<number>();
  const [submiting, setSubmiting] = useState(false);

  const [showOrgnization, setShowOrgnization] = useState(false);

  const [emailCountdown] = useCountDown({
    targetDate: emailDate,
    onEnd: () => {},
  });
  function handleCancel() {
    cancel();
  }

  async function onSubmit() {
    if (!formRef.current) {
      return;
    }

    try {
      const values = await formRef.current.validate();

      if (values.email && values.code) {
        const params: LoginPayloadModel = {
          email: values.email.trim(),
          code: values.code.trim(),
          login_source: "geo_flow_server",
        };

        if (values.organization) {
          params.geo_flow_org_name = values.organization.trim();
        }

        setSubmiting(true);
        const { code, data, message } = await loginApi(params);
        if (code === 200) {
          Toast.success({
            content: message,
            showClose: false,
          });

          setLocalStorage(LoginEnum.Token, data);
          dispatch(setToken(data));
          submit();
        }
        setSubmiting(false);
      }
    } catch (e) {
      setSubmiting(false);
    }
  }

  function handleSubmit() {
    onSubmit();
  }

  async function sendEmail(email: string) {
    try {
      setIsSendEmail(true);
      const { code, data, message } = await checkNeedOrgnizationApi(
        encrypt(email)
      );

      setIsSendEmail(false);
      if (code === 200) {
        setEmailDate(Date.now() + 60000);
        Toast.success(message);
      }

      if (code === 200 && data.show_org_collect) {
        setShowOrgnization(true);
      }
    } catch (e) {
      setIsSendEmail(false);
    }
  }

  function sendEmailCode() {
    const email = formRef.current?.getValue("email");
    if (email) {
      sendEmail(email);
    }
  }

  return (
    <Modal
      title={t("loginModal.title")}
      visible={true}
      onCancel={handleCancel}
      closeOnEsc={true}
      width="448px"
      centered
      cancelText={t("loginModal.cancelText")}
      okText={t("loginModal.okText")}
      onOk={handleSubmit}
      confirmLoading={submiting}
      cancelButtonProps={{
        autoFocus: false,
      }}
    >
      <Form
        autoComplete="off"
        getFormApi={(formApi) => (formRef.current = formApi)}
      >
        {({ formState }: { formState: FormState<LoginJobPayloadModel> }) => (
          <>
            <Form.Input
              autoFocus
              label={{
                text: t("loginModal.emailLabel"),
                required: false,
              }}
              trigger="blur"
              validate={asyncEmailValidate}
              field="email"
              placeholder={t("loginModal.emailPlaceholder")}
            />
            <Form.Input
              label={{
                text: t("loginModal.verificationLabel"),
                required: false,
              }}
              validate={codeValidate}
              field="code"
              trigger="blur"
              className={styles.codeWrapper}
              addonAfter={
                <Button
                  className={styles.sendBtn}
                  theme="borderless"
                  style={{
                    boxShadow: "none",
                    height: "30px",
                    borderRadius: "0 4px 4px 0",
                  }}
                  disabled={
                    isSendEmail ||
                    !formState.touched?.email ||
                    emailCountdown !== 0 ||
                    !!formState.errors?.email
                  }
                  onClick={sendEmailCode}
                  size="small"
                >
                  {emailCountdown !== 0
                    ? `${t("loginModal.resend")} (${Math.round(
                        emailCountdown / 1000
                      )}s)`
                    : t("loginModal.send")}
                </Button>
              }
              placeholder={t("loginModal.verificationPlaceholder")}
            />

            {showOrgnization && (
              <Form.Input
                label={{
                  text: t("loginModal.organizationLabel"),
                  required: false,
                }}
                validate={organizationValidate}
                trigger="blur"
                field="organization"
                placeholder={t("loginModal.organizationPlaceholder")}
              />
            )}
          </>
        )}
      </Form>
    </Modal>
  );
};

export default LoginDialog;
